import {
  ALPHABET,
  ALPHABET_SIZE,
  foldDiacritics,
  keywordAlphabet,
  letterIndex,
  mapLetters,
} from "@/lib/tool-kit/ciphers/alphabet";
import { ENGLISH_LETTER_FREQUENCIES } from "@/lib/tool-kit/ciphers/english-frequency";

/**
 * The monoalphabetic substitution cipher, and the tools for solving one by hand.
 *
 * A key is a cipher alphabet: the 26 letters in some order, written under A–Z. Plain A
 * becomes the key's first letter, B its second, and so on. A keyword key is the
 * keyword's letters (each once) followed by the rest of the alphabet in order — the
 * classic "keyword cipher" — and a random key is a shuffle.
 *
 * Solving works the other way round, so the solver's state is a *mapping* from cipher
 * letters to the plain letters the reader has guessed for them. By the convention of
 * puzzle books and of the American Cryptogram Association, cipher text is written in
 * capitals and the plain text recovered from it in lower case.
 */

/** A source of whole numbers in [0, bound). The widget passes a `crypto` one. */
export type RandomInt = (bound: number) => number;

const UINT32_RANGE = 0x1_00_00_00_00;

/** An unbiased random integer in [0, bound) from the browser's secure generator. */
export function secureRandomInt(bound: number): number {
  const limit = UINT32_RANGE - (UINT32_RANGE % bound);
  const buffer = new Uint32Array(1);
  let value = limit;
  while (value >= limit) {
    globalThis.crypto.getRandomValues(buffer);
    value = buffer[0];
  }
  return value % bound;
}

/* -------------------------------------------------------------------- keys */

/** The cipher alphabet for a keyword: ZEBRAS → ZEBRASCDFGHIJKLMNOPQTUVWXY. */
export function keywordKey(keyword: string): string {
  return keywordAlphabet(keyword, ALPHABET);
}

/**
 * A random cipher alphabet in which no letter stands for itself — the rule newspaper
 * cryptograms follow, because a letter left in place gives the solver a free answer.
 * A Fisher–Yates shuffle, repeated until it has no fixed point (about one try in 2.7).
 */
export function randomKey(random: RandomInt = secureRandomInt): string {
  for (;;) {
    const letters = [...ALPHABET];
    for (let index = letters.length - 1; index > 0; index -= 1) {
      const swap = random(index + 1);
      [letters[index], letters[swap]] = [letters[swap], letters[index]];
    }
    if (letters.every((letter, index) => letter !== ALPHABET.charAt(index))) {
      return letters.join("");
    }
  }
}

export interface KeyReading {
  /** The 26-letter key in capitals, or null while it is not a full alphabet. */
  key: string | null;
  /** Letters of A–Z the key does not contain. */
  missing: string[];
  /** Letters the key contains more than once. */
  repeated: string[];
}

/** Check a typed key: exactly the 26 letters, each once. Non-letters are ignored. */
export function readKey(raw: string): KeyReading {
  const letters = [...foldDiacritics(raw).toUpperCase()].filter(
    (char) => letterIndex(char) >= 0
  );
  const counts = new Map<string, number>();
  for (const letter of letters) {
    counts.set(letter, (counts.get(letter) ?? 0) + 1);
  }
  const missing = [...ALPHABET].filter((letter) => !counts.has(letter));
  const repeated = [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([letter]) => letter)
    .sort();
  const complete =
    letters.length === ALPHABET_SIZE &&
    missing.length === 0 &&
    repeated.length === 0;
  return { key: complete ? letters.join("") : null, missing, repeated };
}

/** Plain letter i becomes key letter i. Case and non-letters are kept. */
export function substitutionEncode(text: string, key: string): string {
  return mapLetters(text, (index) => letterIndex(key.charAt(index)));
}

/** The inverse: a cipher letter becomes the plain letter it stands under. */
export function substitutionDecode(text: string, key: string): string {
  const inverse: number[] = Array.from({ length: ALPHABET_SIZE }, () => 0);
  for (const [plain, letter] of [...key].entries()) {
    inverse[letterIndex(letter)] = plain;
  }
  return mapLetters(text, (index) => inverse[index]);
}

/* -------------------------------------------------------------- frequencies */

/** How many times each letter A–Z occurs, case and accents ignored. */
export function letterCounts(text: string): number[] {
  const counts: number[] = Array.from({ length: ALPHABET_SIZE }, () => 0);
  for (const char of foldDiacritics(text)) {
    const index = letterIndex(char);
    if (index >= 0) {
      counts[index] += 1;
    }
  }
  return counts;
}

/** Letters by count, most frequent first; ties in alphabetical order. */
export function rankLetters(counts: readonly number[]): string[] {
  return [...ALPHABET].sort(
    (left, right) =>
      counts[letterIndex(right)] - counts[letterIndex(left)] ||
      left.localeCompare(right)
  );
}

/** English letters, most frequent first: E T A O I N S H R D L C U M W F G Y P B V K J X Q Z. */
export const ENGLISH_BY_FREQUENCY: readonly string[] = rankLetters(
  ENGLISH_LETTER_FREQUENCIES
);

/** English frequency of a letter as a proportion (E ≈ 0.127). */
export function englishShare(letter: string): number {
  return ENGLISH_LETTER_FREQUENCIES[letterIndex(letter)] ?? 0;
}

/* ------------------------------------------------------------------ mapping */

/**
 * Cipher letter → guessed plain letter, as a 26-entry array indexed by the cipher
 * letter (A = 0). "" is "no guess yet". Plain letters are stored in capitals.
 */
export type Mapping = readonly string[];

export const EMPTY_MAPPING: Mapping = Array.from(
  { length: ALPHABET_SIZE },
  () => ""
);

/** Set or clear one guess. Anything but a single A–Z letter clears it. */
export function setGuess(
  mapping: Mapping,
  cipher: string,
  plain: string
): Mapping {
  const index = letterIndex(cipher);
  if (index < 0) {
    return mapping;
  }
  const letter = foldDiacritics(plain).toUpperCase().slice(-1);
  const next = [...mapping];
  next[index] = letterIndex(letter) >= 0 ? letter : "";
  return next;
}

/** Plain letters guessed for more than one cipher letter — one of them is wrong. */
export function mappingConflicts(mapping: Mapping): Set<string> {
  const seen = new Set<string>();
  const conflicts = new Set<string>();
  for (const plain of mapping) {
    if (plain === "") {
      continue;
    }
    if (seen.has(plain)) {
      conflicts.add(plain);
    }
    seen.add(plain);
  }
  return conflicts;
}

/**
 * A first guess from frequency alone: the commonest cipher letter is taken for E, the
 * next for T, and so on down ETAOIN SHRDLU. Letters that do not occur get no guess. On a
 * short text this is wrong in most places — it is a starting point to correct.
 */
export function frequencyGuess(counts: readonly number[]): Mapping {
  const next = [...EMPTY_MAPPING];
  for (const [rank, letter] of rankLetters(counts).entries()) {
    if (counts[letterIndex(letter)] > 0) {
      next[letterIndex(letter)] = ENGLISH_BY_FREQUENCY[rank];
    }
  }
  return next;
}

/** One character of the partial decryption. */
export interface SolvedChar {
  /** The cipher letter in capitals, or the character itself when it is not a letter. */
  cipher: string;
  /** The guessed plain letter in lower case; "" when there is no guess. */
  plain: string;
  letter: boolean;
}

export function solveChars(text: string, mapping: Mapping): SolvedChar[] {
  return [...foldDiacritics(text)].map((char) => {
    const index = letterIndex(char);
    if (index < 0) {
      return { cipher: char, plain: "", letter: false };
    }
    return {
      cipher: ALPHABET.charAt(index),
      plain: mapping[index].toLowerCase(),
      letter: true,
    };
  });
}

/**
 * The text with every guessed letter replaced by its plain letter in lower case, and
 * every letter not yet guessed left as its cipher letter in capitals.
 */
export function partialDecryption(text: string, mapping: Mapping): string {
  return solveChars(text, mapping)
    .map((char) => (char.letter ? char.plain || char.cipher : char.cipher))
    .join("");
}

/* -------------------------------------------------------------- word hints */

/*
 * The commonest English words of one, two and three letters, and the commonest doubled
 * letters, most frequent first — what a solver tries first on the short words and the
 * doubled pairs of a cryptogram. Ranked by CitationEasy from a count of about 7 million
 * words of public-domain books (the same 70 Project Gutenberg titles behind the
 * password checker's word list).
 */
export const SHORT_WORDS: Readonly<Record<1 | 2 | 3, readonly string[]>> = {
  1: ["A", "I"],
  2: [
    "OF",
    "TO",
    "IN",
    "IT",
    "HE",
    "AS",
    "IS",
    "BE",
    "AT",
    "ON",
    "MY",
    "BY",
    "ME",
    "SO",
    "OR",
    "NO",
    "WE",
    "IF",
    "AN",
    "UP",
    "DO",
    "US",
    "GO",
    "AM",
  ],
  3: [
    "THE",
    "AND",
    "WAS",
    "HIS",
    "FOR",
    "YOU",
    "HAD",
    "HER",
    "NOT",
    "BUT",
    "SHE",
    "HIM",
    "ALL",
    "ONE",
    "ARE",
    "OUT",
    "WHO",
    "ANY",
    "NOW",
    "MAN",
    "HAS",
    "CAN",
    "DID",
    "MAY",
    "SEE",
    "ITS",
    "OUR",
    "HOW",
    "TWO",
    "OWN",
    "WAY",
    "SAY",
    "OLD",
    "TOO",
    "DAY",
    "MEN",
    "YET",
    "SAW",
    "OFF",
    "LET",
    "NEW",
    "GOT",
    "GET",
    "PUT",
  ],
};

/** Doubled letters inside English words, commonest first. */
export const ENGLISH_DOUBLES: readonly string[] = [
  "LL",
  "EE",
  "OO",
  "SS",
  "TT",
  "PP",
  "RR",
  "FF",
  "NN",
  "MM",
  "CC",
  "DD",
  "GG",
];

/** A word's letter pattern: THE → ABC, DID → ABA, SEE → ABB. */
export function wordPattern(word: string): string {
  const seen = new Map<string, string>();
  let pattern = "";
  for (const letter of word) {
    if (!seen.has(letter)) {
      seen.set(letter, ALPHABET.charAt(seen.size));
    }
    pattern += seen.get(letter);
  }
  return pattern;
}

/**
 * Could `cipherWord` stand for `candidate` under the current guesses? The two must share
 * a letter pattern, every guessed letter must agree, and a letter not yet guessed may
 * not take a plain letter that another cipher letter already has.
 */
export function fitsMapping(
  cipherWord: string,
  candidate: string,
  mapping: Mapping
): boolean {
  if (
    cipherWord.length !== candidate.length ||
    wordPattern(cipherWord) !== wordPattern(candidate)
  ) {
    return false;
  }
  const taken = new Set(mapping.filter((plain) => plain !== ""));
  for (const [position, cipher] of [...cipherWord].entries()) {
    const plain = candidate.charAt(position);
    const guess = mapping[letterIndex(cipher)];
    if (guess !== "" ? guess !== plain : taken.has(plain)) {
      return false;
    }
  }
  return true;
}

export interface WordHint {
  /** The cipher word in capitals. */
  word: string;
  count: number;
  /** English words it could be under the current guesses, commonest first. */
  candidates: string[];
}

/** Runs of letters and apostrophes: WON'T stays one word, not WON and T. */
const WORD_SPLIT = /[^A-Z'\u2019]+/;
const APOSTROPHE = /['\u2019]/;

function cipherWords(text: string): string[] {
  return foldDiacritics(text)
    .toUpperCase()
    .split(WORD_SPLIT)
    .filter((word) => word !== "" && !APOSTROPHE.test(word));
}

/**
 * Every cipher word of one to three letters, most frequent first, with the English
 * words it could still be. Only meaningful when the cipher text keeps its spaces.
 * Words with an apostrophe are left out: the T of WON'T is not a one-letter word.
 */
export function shortWordHints(text: string, mapping: Mapping): WordHint[] {
  const counts = new Map<string, number>();
  for (const word of cipherWords(text)) {
    if (word.length <= 3) {
      counts.set(word, (counts.get(word) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort(
      ([leftWord, left], [rightWord, right]) =>
        right - left ||
        leftWord.length - rightWord.length ||
        leftWord.localeCompare(rightWord)
    )
    .map(([word, count]) => ({
      word,
      count,
      candidates: SHORT_WORDS[word.length as 1 | 2 | 3].filter((candidate) =>
        fitsMapping(word, candidate, mapping)
      ),
    }));
}

export interface DoubleHint {
  /** The doubled cipher pair, e.g. "QQ". */
  pair: string;
  count: number;
}

/** Doubled letters inside the cipher words, most frequent first. */
export function doubledLetters(text: string): DoubleHint[] {
  const counts = new Map<string, number>();
  for (const word of cipherWords(text)) {
    for (let index = 1; index < word.length; index += 1) {
      if (word.charAt(index) === word.charAt(index - 1)) {
        const pair = word.slice(index - 1, index + 1);
        counts.set(pair, (counts.get(pair) ?? 0) + 1);
      }
    }
  }
  return [...counts.entries()]
    .map(([pair, count]) => ({ pair, count }))
    .sort(
      (left, right) =>
        right.count - left.count || left.pair.localeCompare(right.pair)
    );
}

/** Guess every letter of `cipherWord` as the matching letter of `plainWord`. */
export function applyWord(
  mapping: Mapping,
  cipherWord: string,
  plainWord: string
): Mapping {
  let next = mapping;
  for (const [position, cipher] of [...cipherWord].entries()) {
    next = setGuess(next, cipher, plainWord.charAt(position));
  }
  return next;
}
